import { useEffect } from "react";
import { captureException } from "@/lib/sentry";

/**
 * Captures unhandled promise rejections and window.onerror events,
 * forwarding them to Sentry and logging to console.
 *
 * Usage: call once at the app root (e.g. inside <App />).
 */
export function useErrorReporting() {
  useEffect(() => {
    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;
      void captureException(reason instanceof Error ? reason : new Error(String(reason)), {
        extra: { type: "unhandled_promise_rejection", url: window.location.href },
      });
      console.error("[ErrorReporting] Unhandled rejection:", reason?.message ?? reason);
    }

    function handleGlobalError(event: ErrorEvent) {
      void captureException(event.error ?? new Error(event.message), {
        extra: {
          type: "uncaught_error",
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          url: window.location.href,
        },
      });
      console.error("[ErrorReporting] Uncaught error:", event.message);
    }

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleGlobalError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleGlobalError);
    };
  }, []);
}
